// src/components/Shared/DownloadButton.tsx
import React, { useState } from 'react';
import { Download, Archive } from 'lucide-react';
import { getUnifiedDownloadLink, ToolType } from '../../utils/downloadUtils';

interface DownloadButtonProps {
    fileId?: string;
    resultId?: string;
    result?: any;      // Flexible result object used by different tools
    fileName?: string;
    children?: React.ReactNode;
    variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost' | string;
    disabled?: boolean;
    isBatch?: boolean;
    toolType?: string | null;
    onDownloadComplete?: ((id: string) => void) | null;
}

const DownloadButton: React.FC<DownloadButtonProps> = ({
    fileId,
    resultId,
    result,
    fileName,
    children,
    variant = 'primary',
    disabled = false,
    isBatch = false,
    toolType = null,
    onDownloadComplete = null
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Extract resultId and fileName from multiple possible sources
    let downloadId: string | null = null;
    let displayFileName: string | undefined = fileName;

    if (result) {
        downloadId = result.resultId || resultId || fileId || null;
        displayFileName = result.fileName || fileName;
    } else {
        downloadId = resultId || fileId || null;
    }

    // Handle tool type detection
    let detectedToolType: ToolType | null = toolType as ToolType | null;

    // Try to determine tool type from result object if not explicitly provided
    if (!detectedToolType && result) {
        if (result.hasOwnProperty('reductionPercentage') || result.hasOwnProperty('percentReduction')) {
            detectedToolType = 'reducer';
        } else if (result.hasOwnProperty('fileCount') || (result.hasOwnProperty('outputFormat') && !result.hasOwnProperty('parts'))) {
            detectedToolType = 'operations';
        } else if (result.hasOwnProperty('parts') || (result.hasOwnProperty('files') && Array.isArray(result.files))) {
            detectedToolType = 'operations';
        }
    }

    const baseBtnClasses = "flex items-center justify-center gap-[10px] px-6 py-3 rounded-md text-base font-semibold cursor-pointer transition-all duration-200 border-none outline-none relative min-w-[180px] disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(0,0,0,0.15)] dark:hover:shadow-[0_4px_12px_rgba(37,99,235,0.4)] max-md:w-full max-md:py-[14px] max-md:px-5 max-md:text-[15px]";

    const variantClasses = variant === 'secondary'
        ? "bg-gray-100 text-gray-800 border border-solid border-gray-300 hover:bg-gray-200"
        : "bg-blue-600 text-white hover:bg-blue-700";

    const spinnerClasses = `w-5 h-5 border-2 border-solid rounded-full animate-spin mr-[10px] ${variant === 'secondary'
        ? "border-gray-800/30 border-t-gray-800"
        : "border-white/30 border-t-white"
        }`;

    // Determine file type for icon selection
    const getFileType = (): string | null => {
        if (!displayFileName) return null;
        const extension = displayFileName.split('.').pop()?.toLowerCase();
        return extension || null;
    };

    // Get appropriate icon based on file type
    const getFileIcon = () => {
        const fileType = getFileType();
        if (isBatch || fileType === 'zip') {
            return <Archive size={18} className="mr-[6px]" />;
        }
        return <Download size={18} className="mr-[6px]" />;
    };

    // Get appropriate button text based on file type
    const getButtonText = () => {
        if (children) return children;
        const fileType = getFileType();
        if (isBatch || fileType === 'zip') {
            return 'Download ZIP';
        }
        return 'Download';
    };

    const handleDownload = async () => {
        if (!downloadId) {
            setError("No result ID available for download");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const timestamp = Date.now();
            const downloadUrl = getUnifiedDownloadLink(downloadId, detectedToolType || undefined);

            // Create a temporary hidden anchor element for download
            const link = document.createElement('a');
            link.href = `${downloadUrl}?t=${timestamp}`;
            link.download = displayFileName || 'download';
            link.style.display = 'none';
            document.body.appendChild(link);

            // Trigger the download
            link.click();

            // Clean up
            setTimeout(() => {
                if (document.body.contains(link)) {
                    document.body.removeChild(link);
                }
                setLoading(false);

                // Call download complete callback if provided
                if (onDownloadComplete && typeof onDownloadComplete === 'function') {
                    onDownloadComplete(downloadId!);
                }
            }, 2000);

        } catch (err) {
            console.error("DOWNLOAD ERROR:", err);
            setError("Failed to download file. Please try again.");
            setLoading(false);
        }
    };

    return (
        <button
            className={`${baseBtnClasses} ${variantClasses}`}
            onClick={handleDownload}
            disabled={disabled || loading}
            type="button"
        >
            {loading ? (
                <div className={spinnerClasses}></div>
            ) : (
                <>
                    {getFileIcon()}
                    {getButtonText()}
                </>
            )}
            {error && <div className="text-red-500 text-xs mt-1 text-center">{error}</div>}
        </button>
    );
};

export default DownloadButton;
